import axios from "axios";
import { UseFormSetError } from "react-hook-form";

import { AlertTypes } from "@/utils/atoms";
import { getAuthApiErrorText } from "@/utils/authApiError";

type InitialPasswordSetupErrorType =
  | "UNAUTHORIZED"
  | "USER_NOT_FOUND"
  | "INVALID_PASSWORD"
  | "SERVER_ERROR"
  | "NETWORK_ERROR"
  | "UNKNOWN_ERROR";

const INITIAL_PASSWORD_SETUP_ERROR_MESSAGES = {
  UNAUTHORIZED:
    "translation:newWelcome.initialPasswordSetup.errors.unauthorized",
  USER_NOT_FOUND:
    "translation:newWelcome.initialPasswordSetup.errors.userNotFound",
  INVALID_PASSWORD: "translation:label.validation.password",
  SERVER_ERROR:
    "translation:newWelcome.initialPasswordSetup.errors.genericError",
  NETWORK_ERROR:
    "translation:newWelcome.initialPasswordSetup.errors.genericError",
  UNKNOWN_ERROR:
    "translation:newWelcome.initialPasswordSetup.errors.genericError",
} as const;

const PASSWORD_ERROR_PATTERNS = [
  "password",
  "invalid credentials",
  "bad credentials",
] as const;

export const determineInitialPasswordSetupErrorType = (
  error: unknown,
): InitialPasswordSetupErrorType => {
  if (!axios.isAxiosError(error)) {
    return "UNKNOWN_ERROR";
  }

  if (error.code === "ECONNABORTED" || !error.response) {
    return "NETWORK_ERROR";
  }

  const status = error.response.status;
  const message = getAuthApiErrorText(error);

  if (status === 401 || status === 403) {
    return "UNAUTHORIZED";
  }

  if (status === 404 || message.includes("no user found")) {
    return "USER_NOT_FOUND";
  }

  if (
    status === 400 &&
    PASSWORD_ERROR_PATTERNS.some((pattern) => message.includes(pattern))
  ) {
    return "INVALID_PASSWORD";
  }

  if (status >= 500) {
    return "SERVER_ERROR";
  }

  return "UNKNOWN_ERROR";
};

export const createInitialPasswordSetupErrorHandler = (
  setError: UseFormSetError<{ confirmPassword: string; password: string }>,
  t: (key: string) => string,
  showAlert: (alert: { text: string; type: AlertTypes }) => void,
) => {
  return (error: unknown) => {
    const errorType = determineInitialPasswordSetupErrorType(error);

    switch (errorType) {
      case "INVALID_PASSWORD":
        setError("password", {
          type: "backendError",
          message: t(INITIAL_PASSWORD_SETUP_ERROR_MESSAGES.INVALID_PASSWORD),
        });
        break;

      case "UNAUTHORIZED":
      case "USER_NOT_FOUND":
      case "SERVER_ERROR":
      case "NETWORK_ERROR":
      case "UNKNOWN_ERROR":
        showAlert({
          text: t(INITIAL_PASSWORD_SETUP_ERROR_MESSAGES[errorType]),
          type: AlertTypes.error,
        });
        break;
    }
  };
};
